/**
 * Where a visitor is going — one destination or several.
 *
 * A single registration can cover several unidades or departamentos: a courier with parcels for
 * three units, an inspector doing three departamentos, a technician on one call-out. Each
 * destination becomes its own role server-side (condo-manager's services/visit_destinations.go
 * explains why), sharing one visit group so the entry, the exit and a revocation move together.
 *
 * These rules sit outside the registration modal because the same picker exists on web, Android
 * and iOS, and because the backend applies the matching ones — a destination the browser keeps
 * and the server drops is a parcel nobody is expecting.
 */

export interface VisitDestinationChoice {
  /** The unidade's or departamento's id. */
  id: string;
  /** What the porteiro sees: "101", "Padaria do Zé". */
  label: string;
  kind: 'unit' | 'department';
  /** Who answered for the visit at THIS destination, when somebody did. */
  responsiblePersonId?: string;
}

/**
 * A guard against a stuck finger, not a product limit.
 *
 * Every destination is a role, a face enrolled on the readers and a row on a unidade's list, so
 * a visit to forty units is a mistake somebody should be stopped from saving — while a delivery
 * round of a dozen is ordinary.
 */
export const MAX_VISIT_DESTINATIONS = 12;

const keyOf = (destination: VisitDestinationChoice) => `${destination.kind}:${destination.id}`;

/** Adds a destination unless it is already there — a double-tap must not create two roles. */
export function addDestination(
  destinations: VisitDestinationChoice[],
  destination: VisitDestinationChoice
): VisitDestinationChoice[] {
  const existing = new Set((destinations ?? []).map(keyOf));
  if (existing.has(keyOf(destination))) return destinations;
  return [...(destinations ?? []), destination];
}

export function removeDestination(
  destinations: VisitDestinationChoice[],
  id: string
): VisitDestinationChoice[] {
  return (destinations ?? []).filter(destination => destination.id !== id);
}

export function canAddDestination(destinations: VisitDestinationChoice[]): boolean {
  return (destinations ?? []).length < MAX_VISIT_DESTINATIONS;
}

/**
 * The destination the registration form's own unidade/departamento picker is on.
 *
 * That picker is where a visit has always said where it is going, and it stays the primary
 * destination: the first role of the group carries the request — its liberação, its responsible
 * person, its place in the unit's list. An empresa picks departamentos through the same control,
 * which is why the customer type decides the kind here rather than at every call site.
 */
export function primaryDestinationOf(
  selected: { value: string; label: string } | null | undefined,
  customerType: string | undefined
): VisitDestinationChoice | null {
  if (!selected?.value) return null;
  const isEmpresa = customerType === 'empresa' || customerType === 'fabrica';
  return {
    id: selected.value,
    label: selected.label,
    kind: isEmpresa ? 'department' : 'unit',
  };
}

/**
 * Every destination of the visit: the picker's own first, then the others.
 *
 * The form holds the two separately — the picker it has always had, and the list of extras — and
 * this is the single place they become one visit. The primary is never repeated among the extras:
 * two entries for one unidade would register the visitor there twice.
 */
export function destinationsOfVisit(
  primary: VisitDestinationChoice | null,
  extras: VisitDestinationChoice[]
): VisitDestinationChoice[] {
  if (!primary) return extras ?? [];
  return (extras ?? []).reduce(addDestination, [primary]);
}

/**
 * What an edit made on the list means for the picker and for the extras.
 *
 * Removing the destination the picker is showing promotes the next one: the list and the picker
 * describe the same visit, and leaving the picker on a destination the operator just deleted
 * would save a visit to a unidade that is not on the list in front of them.
 */
export function applyDestinationsChange(
  next: VisitDestinationChoice[],
  primary: VisitDestinationChoice | null
): { primary: VisitDestinationChoice | null; extras: VisitDestinationChoice[] } {
  const remaining = next ?? [];
  const keptPrimary = primary && remaining.some(destination => keyOf(destination) === keyOf(primary));
  if (keptPrimary) {
    return {
      primary,
      extras: remaining.filter(destination => keyOf(destination) !== keyOf(primary!)),
    };
  }
  const [promoted, ...rest] = remaining;
  return { primary: promoted ?? null, extras: rest };
}

/**
 * The `destinations` array the create call carries.
 *
 * Empty for a single destination: the ordinary registration keeps its exact payload, and the
 * backend its exact behaviour, for the overwhelming majority of visits.
 */
export function destinationsPayload(
  destinations: VisitDestinationChoice[]
): Array<{ unit_id?: string; department_id?: string; responsible_person_id?: string }> {
  if (!destinations || destinations.length < 2) return [];

  return destinations.map(destination => ({
    ...(destination.kind === 'unit' ? { unit_id: destination.id } : { department_id: destination.id }),
    ...(destination.responsiblePersonId ? { responsible_person_id: destination.responsiblePersonId } : {}),
  }));
}
